import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';
import { MultiSelect } from '@/components/MultiSelect';
import { DateTimePicker } from '@/components/DateTimePicker';
import { OnSceneLocationControl } from '@/components/callcard/OnSceneLocationControl';
import { CALL_CARD_PRIORITY_OPTIONS } from '@/constants/priorities';

interface CallCardDetailsFormProps {
  callCard: any;
  isReadOnly?: boolean;
  callCardNotClosed?: boolean;
  isEditingClosedCallCard?: boolean;
  /** MDT officer on this call: may confirm an approximate location from the device's GPS. */
  canConfirmOnSceneLocation?: boolean;
  /** Passes the call's edit gate: may remove an on-scene confirmation or stop a learned location being used. */
  canEditCallLocation?: boolean;
  natureOfCallSopLink?: string;
  clientLink?: string;
  callLocationMapLink?: string;
  patrolZoneNames?: string[];
  isShowingClientAddressButton?: boolean;
  natureOfCallOptions?: any[];
  natureOfCallGroupBy?: string | null;
  clientOptions?: any[];
  siteOptions?: any[];
  sitesLoading?: boolean;
  priorityOptions?: any[];
  onNatureChange?: (data: { values: any[]; items: any[] }) => void;
  onClientChange?: (data: { value: number | null; item: any }) => void;
  onSiteChange?: (data: { values: any[]; items: any[] }) => void;
  onFieldChange?: (data: { field: string; value: any }) => void;
  onLocationBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onClientAddressClick?: (onComplete?: (data: any) => void) => void;
  onDisabledFieldClick?: () => void;
  priorityAutoChangeVersion?: number;
  onNatureOpen?: () => void;
  onNatureClose?: () => void;
}

const DEFAULT_PRIORITY_OPTIONS = CALL_CARD_PRIORITY_OPTIONS;

export function CallCardDetailsForm({
  callCard,
  isReadOnly = false,
  callCardNotClosed = true,
  isEditingClosedCallCard = false,
  canConfirmOnSceneLocation = false,
  canEditCallLocation = false,
  natureOfCallSopLink = '',
  clientLink = '',
  callLocationMapLink = '',
  patrolZoneNames = [],
  isShowingClientAddressButton = false,
  natureOfCallOptions = [],
  natureOfCallGroupBy = null,
  clientOptions = [],
  siteOptions = [],
  sitesLoading = false,
  priorityOptions = DEFAULT_PRIORITY_OPTIONS,
  onNatureChange,
  onClientChange,
  onSiteChange,
  onFieldChange,
  onLocationBlur,
  onClientAddressClick,
  onDisabledFieldClick,
  priorityAutoChangeVersion,
  onNatureOpen,
  onNatureClose,
}: CallCardDetailsFormProps) {
  const { theme } = useTheme();

  const [localNatureOfCall, setLocalNatureOfCall] = useState<any[]>([]);
  const [localClientId, setLocalClientId] = useState<number | null>(null);
  const [localSites, setLocalSites] = useState<any[]>([]);
  const localClientIdRef = useRef(localClientId);
  localClientIdRef.current = localClientId;
  const [localDateTime, setLocalDateTime] = useState<Date | null>(null);
  const [localPriority, setLocalPriority] = useState<number | null>(callCard?.priority ?? null);
  const [isDateTimePickerOpen, setIsDateTimePickerOpen] = useState(false);

  // Local state for text fields — controlled inputs need local state
  // because the parent CallCardDetailsTab is memoized.
  const [localAddress, setLocalAddress] = useState(callCard?.callLocFormattedAddress || '');
  const localAddressRef = useRef(localAddress);
  localAddressRef.current = localAddress;
  const [localAddress2, setLocalAddress2] = useState(callCard?.callLocAddress2 || '');
  const [localBuilding, setLocalBuilding] = useState(callCard?.callLocName || '');
  const [localCaller, setLocalCaller] = useState(callCard?.callerName || '');
  const [localPhone, setLocalPhone] = useState(callCard?.callerPhone || '');
  const [localCallerLocation, setLocalCallerLocation] = useState(callCard?.callerLocation || '');

  const canEdit = useMemo((): boolean => {
    if (isReadOnly) return false;
    return callCardNotClosed || isEditingClosedCallCard;
  }, [isReadOnly, callCardNotClosed, isEditingClosedCallCard]);

  const zoneText = useMemo((): string => {
    if (!patrolZoneNames || patrolZoneNames.length === 0) return '';
    const label = patrolZoneNames.length > 1 ? 'Zones' : 'Zone';
    return `${label}: ${patrolZoneNames.join(', ')}`;
  }, [patrolZoneNames]);

  // Sync text fields from callCard (external updates, e.g. realtime)
  useEffect(() => {
    setLocalAddress(callCard?.callLocFormattedAddress || '');
    setLocalAddress2(callCard?.callLocAddress2 || '');
    setLocalBuilding(callCard?.callLocName || '');
    setLocalCaller(callCard?.callerName || '');
    setLocalPhone(callCard?.callerPhone || '');
    setLocalCallerLocation(callCard?.callerLocation || '');
  }, [callCard?.callLocFormattedAddress, callCard?.callLocAddress2, callCard?.callLocName, callCard?.callerName, callCard?.callerPhone, callCard?.callerLocation]);

  // Sync nature of call from callCard
  useEffect(() => {
    setLocalNatureOfCall(callCard?.natureOfCall ? callCard.natureOfCall.slice() : []);
  }, [callCard?.natureOfCall]);

  // Sync callDateTime from callCard
  useEffect(() => {
    setLocalDateTime(callCard?.callDateTime ? new Date(callCard.callDateTime) : null);
  }, [callCard?.callDateTime]);

  // Sync priority from callCard
  useEffect(() => {
    setLocalPriority(callCard?.priority ?? null);
  }, [callCard?.priority]);

  // Sync client from callCard
  useEffect(() => {
    if (callCard?.client) {
      setLocalClientId(callCard.client.id ?? null);
      setLocalSites(callCard.client.sites ? callCard.client.sites.slice() : []);
    } else {
      setLocalClientId(null);
      setLocalSites([]);
    }
  }, [callCard?.client]);

  // Listen for other pickers opening — only update state if the
  // datetime picker is actually open to avoid unnecessary re-renders.
  const isDateTimePickerOpenRef = useRef(false);
  isDateTimePickerOpenRef.current = isDateTimePickerOpen;

  useEffect(() => {
    const handleOtherPickerOpened = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail && detail.source !== 'callcard-details' && isDateTimePickerOpenRef.current) {
        setIsDateTimePickerOpen(false);
      }
    };
    window.addEventListener('datetime-picker-opened', handleOtherPickerOpened);
    return () => window.removeEventListener('datetime-picker-opened', handleOtherPickerOpened);
  }, []);

  const onNatureOfCallChange = useCallback((values: any[]) => {
    setLocalNatureOfCall(values);
    const items = natureOfCallOptions.filter((opt: any) => values.indexOf(opt.code) !== -1);
    onNatureChange?.({ values, items });
    onFieldChange?.({ field: 'natureOfCall', value: values });
  }, [natureOfCallOptions, onNatureChange, onFieldChange]);

  const onDateTimeChange = useCallback((value: Date | null) => {
    setLocalDateTime(value);
    onFieldChange?.({ field: 'callDateTime', value });
  }, [onFieldChange]);

  const openDateTimePicker = useCallback(() => {
    if (canEdit) {
      window.dispatchEvent(new CustomEvent('datetime-picker-opened', { detail: { source: 'callcard-details' } }));
      setIsDateTimePickerOpen(true);
    }
  }, [canEdit]);

  const closeDateTimePicker = useCallback(() => {
    setIsDateTimePickerOpen(false);
  }, []);

  const handleClientChange = useCallback((value: string | number | null) => {
    const numValue = value !== null ? Number(value) : null;
    setLocalClientId(numValue);
    setLocalSites([]);
    onSiteChange?.({ values: [], items: [] });
    const clientItem = clientOptions.find((c: any) => c.id === value);
    onClientChange?.({ value: numValue, item: clientItem });
  }, [clientOptions, onClientChange, onSiteChange]);

  const handleSiteChange = useCallback((values: any[]) => {
    setLocalSites(values);
    const items = siteOptions.filter((opt: any) => values.indexOf(opt.id) !== -1);

    // Auto-populate location if empty (use refs to always read latest value)
    if (items.length > 0 && !localAddressRef.current?.trim()) {
      const firstSite = items[0];
      if (firstSite.location) {
        setLocalAddress(firstSite.location);
      }
    }

    // Auto-select client if none selected
    if (items.length > 0 && !localClientIdRef.current) {
      const siteWithClient = items.find((s: any) => s.npsdClientId);
      if (siteWithClient) {
        setLocalClientId(siteWithClient.npsdClientId);
      }
    }

    onSiteChange?.({ values, items });
  }, [siteOptions, onSiteChange]);

  const handleLocationBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    onLocationBlur?.(event);
  }, [onLocationBlur]);

  const handleClientAddressClick = useCallback(() => {
    onClientAddressClick?.((addressInfo: any) => {
      setLocalAddress(addressInfo.locFormattedAddress || '');
      setLocalAddress2(addressInfo.locAddress2 || '');
      setLocalBuilding(addressInfo.locName || '');
    });
  }, [onClientAddressClick]);

  const updateField = useCallback((field: string, value: any) => {
    // Update local state for immediate input feedback
    const setters: Record<string, (v: string) => void> = {
      callLocFormattedAddress: setLocalAddress,
      callLocAddress2: setLocalAddress2,
      callLocName: setLocalBuilding,
      callerName: setLocalCaller,
      callerPhone: setLocalPhone,
      callerLocation: setLocalCallerLocation,
    };
    setters[field]?.(value);
    onFieldChange?.({ field, value });
  }, [onFieldChange]);

  const handlePriorityChange = useCallback((value: string | number | null) => {
    const numValue = value !== null ? Number(value) : null;
    setLocalPriority(numValue);
    updateField('priority', numValue);
  }, [updateField]);

  // Brief flash on the priority field when it auto-changes from a NOC selection.
  const [isPriorityFlashing, setIsPriorityFlashing] = useState(false);
  const lastFlashedVersionRef = useRef<number | undefined>(priorityAutoChangeVersion);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (priorityAutoChangeVersion === undefined) return;
    if (priorityAutoChangeVersion === lastFlashedVersionRef.current) return;
    lastFlashedVersionRef.current = priorityAutoChangeVersion;
    setIsPriorityFlashing(true);
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = setTimeout(() => {
      setIsPriorityFlashing(false);
      flashTimeoutRef.current = null;
    }, 1800);
    return () => {
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    };
  }, [priorityAutoChangeVersion]);

  const handleOverlayClick = useCallback(() => {
    onDisabledFieldClick?.();
  }, [onDisabledFieldClick]);

  return (
    <div className={`details-panel ${theme} ${isReadOnly ? 'is-read-only' : ''}`}>
      <div className="panel-header">Call Details</div>
      <div className="panel-body">
        {/* Transparent overlay to capture clicks when disabled */}
        {isReadOnly && (
          <div className="disabled-overlay" onClick={handleOverlayClick}></div>
        )}

        {/* Nature of Call */}
        <div className="form-row">
          <div className="form-group form-group-full">
            <label className="field-label required">
              Nature
              {canEdit && natureOfCallSopLink && (
                <span className="label-link" dangerouslySetInnerHTML={{ __html: natureOfCallSopLink }}></span>
              )}
            </label>
            <MultiSelect
              value={localNatureOfCall}
              items={natureOfCallOptions}
              labelKey="value"
              valueKey="code"
              groupBy={natureOfCallGroupBy}
              groupOrder="insertion"
              placeholder="Select Nature Of Call..."
              disabled={!canEdit}
              sortItems={false}
              onChange={onNatureOfCallChange}
              onOpen={onNatureOpen}
              onClose={onNatureClose}
            />
          </div>
        </div>

        {/* Date/Time & Priority */}
        <div className="form-row">
          <div className="form-group form-group-5">
            <label className="field-label required">Date/Time</label>
            <DateTimePicker
              value={localDateTime}
              type="datetime"
              appendToBody={true}
              clearable={false}
              showSecond={true}
              disabled={!canEdit}
              open={isDateTimePickerOpen}
              onOpenChange={setIsDateTimePickerOpen}
              inputAttr={{ className: 'form-input' }}
              onOpen={openDateTimePicker}
              onClose={closeDateTimePicker}
              onChange={onDateTimeChange}
            />
          </div>
          <div className={`form-group${isPriorityFlashing ? ' priority-auto-flash' : ''}`}>
            <label className="field-label required">Priority</label>
            <Dropdownlist
              value={localPriority}
              items={priorityOptions}
              labelKey="label"
              valueKey="value"
              placeholder="Priority"
              disabled={!canEdit}
              searchable={false}
              sortItems={false}
              onChange={handlePriorityChange}
            />
          </div>
        </div>

        {/* Client & Site */}
        <div className="form-row">
          <div className="form-group">
            <label className="field-label">
              Client
              {clientLink && (
                <span className="label-link" dangerouslySetInnerHTML={{ __html: clientLink }}></span>
              )}
            </label>
            <Dropdownlist
              value={localClientId}
              items={clientOptions}
              labelKey="name"
              valueKey="id"
              placeholder="Select Client  ---  Company"
              disabled={!canEdit}
              searchable={true}
              clearable={true}
              onChange={handleClientChange}
            />
          </div>
          <div className="form-group">
            <label className="field-label">Site</label>
            <MultiSelect
              value={localSites}
              items={siteOptions}
              labelKey="siteName"
              valueKey="id"
              placeholder="Select Sites..."
              disabled={!canEdit}
              loading={sitesLoading}
              onChange={handleSiteChange}
            />
          </div>
        </div>

        {/* Call Location */}
        <div className="form-row">
          <div className="form-group form-group-full">
            <label className="field-label required">
              Call Location
              {callCard.callLocFormattedAddress && callLocationMapLink && (
                <span className="label-link" dangerouslySetInnerHTML={{ __html: callLocationMapLink }}></span>
              )}
            </label>
            <div className="input-with-button">
              <input
                type="text"
                id="txtValidateableLocation"
                name="callLocFormattedAddress"
                className="form-input"
                placeholder="Example: N Scottsdale Rd & E Thompson Peak Pkwy"
                value={localAddress}
                disabled={!canEdit}
                onBlur={handleLocationBlur}
                onChange={(e) => updateField('callLocFormattedAddress', e.target.value)}
              />
              {isShowingClientAddressButton && canEdit && (
                <button
                  type="button"
                  className="btn-input"
                  onClick={handleClientAddressClick}
                  title="Use client's location as the call location"
                >
                  Client Address
                </button>
              )}
            </div>
            {zoneText && <div className="zone-info">{zoneText}</div>}
            <OnSceneLocationControl callCard={callCard} callOpen={callCardNotClosed}
                                    canConfirm={canConfirmOnSceneLocation} canEdit={canEditCallLocation} />
          </div>
        </div>

        {/* Apt/Suite & Building */}
        <div className="form-row">
          <div className="form-group">
            <label className="field-label">Apt. / Suite</label>
            <input
              type="text"
              name="callLocAddress2"
              className="form-input"
              value={localAddress2}
              disabled={!canEdit}
              onChange={(e) => updateField('callLocAddress2', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="field-label">Building</label>
            <input
              type="text"
              name="callLocName"
              className="form-input"
              value={localBuilding}
              disabled={!canEdit}
              onChange={(e) => updateField('callLocName', e.target.value)}
            />
          </div>
        </div>

        {/* Caller & Contact # */}
        <div className="form-row">
          <div className="form-group">
            <label className="field-label">Caller</label>
            <input
              type="text"
              name="callerName"
              className="form-input"
              placeholder="Example: John Smith"
              value={localCaller}
              disabled={!canEdit}
              onChange={(e) => updateField('callerName', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="field-label">Contact #</label>
            <input
              type="text"
              name="callerPhone"
              className="form-input"
              placeholder="Example: 000-000-0000"
              value={localPhone}
              disabled={!canEdit}
              onChange={(e) => updateField('callerPhone', e.target.value)}
            />
          </div>
        </div>

        {/* Caller Location */}
        <div className="form-row">
          <div className="form-group form-group-full">
            <label className="field-label">Caller Location</label>
            <input
              type="text"
              name="callerLocation"
              className="form-input"
              placeholder="Example: N Scottsdale Rd & E Thompson Peak Pkwy S/E Corner"
              value={localCallerLocation}
              disabled={!canEdit}
              onChange={(e) => updateField('callerLocation', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

(window as any).CallCardDetailsFormReact = CallCardDetailsForm;
